// File: components/ResultsDisplay.js
import React, { useState } from 'react';

const ResultsDisplay = ({
  totalSalaries,
  totalEnvironmentCosts,
  totalOperatingCosts,
  totalAdditionalCosts,
  totalCosts,
  location,
  locationMultiplier,
  baseSuccessRate,
  successRateModifier,
  finalSuccessRate,
  formatCurrency,
  resultsInfo,

  // Add these new props for itemized display
  tristenSalaryTier,
  martinSalaryTier,
  salaryTiers,
  tristenFinalSalary,
  martinFinalSalary,

  // Hire details
  hireDesigner,
  designerSalary,
  designerEquity,
  designerPeriods,
  designerEmploymentType,

  eaType,
  eaSalary,
  eaEquity,
  eaPeriods,
  eaEmploymentType,

  hireSocial,
  socialSalary,
  socialEquity,
  socialPeriods,
  socialEmploymentType,

  // Work environment
  workEnvironment,
  workEnvironments,

  // Operating resources
  operatingResources,
  operatingResourcesOptions,

  // Additional costs
  legalCosts,
  travelCosts,
  additionalCosts,

  // Helper functions
  applyLocationMultiplier,
  calculateEffectiveSalaryForPeriods,

  // NEW props for culture and capitalization
  cultureScore,
  capitalizationScore,
  getCapitalizationLevel,
  getCultureLevel,

  // Config objects
  hiresConfig
}) => {
  const [showSuccessDetails, setShowSuccessDetails] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({
    founders: true,
    hires: true,
    workEnv: false,
    operating: false,
    additional: false,
    equity: true
  });

  // Toggle all categories at once
  const toggleAllCategories = (expanded) => {
    setExpandedCategories({
      founders: expanded,
      hires: expanded,
      workEnv: expanded,
      operating: expanded,
      additional: expanded,
      equity: expanded
    });
  };

  // Calculate the average success rate
  const averageSuccessRate = parseFloat(((finalSuccessRate.min + finalSuccessRate.max) / 2).toFixed(1));

  // Helper function to get the appropriate interpretation and color class
  const getInterpretation = (rate) => {
    if (rate < 5) {
      return {
        text: resultsInfo.interpretations?.highRisk || "High Risk: Your current configuration has a very low chance of success. Consider adjusting key factors.",
        colorClass: "text-red-600"
      };
    } else if (rate >= 5 && rate < 10) {
      return {
        text: resultsInfo.interpretations?.challenging || "Challenging: Below average success probability. Look for areas to improve your configuration.",
        colorClass: "text-yellow-600"
      };
    } else if (rate >= 10 && rate < 15) {
      return {
        text: resultsInfo.interpretations?.average || "Average: Your configuration has a typical startup success rate.",
        colorClass: "text-blue-600"
      };
    } else if (rate >= 15 && rate < 20) {
      return {
        text: resultsInfo.interpretations?.aboveAverage || "Above Average: Your configuration is positioned better than most startups.",
        colorClass: "text-green-600"
      };
    } else {
      return {
        text: resultsInfo.interpretations?.excellent || "Excellent: Your configuration has a significantly higher chance of success than the average startup.",
        colorClass: "text-green-700"
      };
    }
  };

  const interpretation = getInterpretation(averageSuccessRate);

  // Helper to toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Helper to display salary with proper formatting - UPDATED to only show selected location
  const displaySalary = (amount) => {
    const adjustedAmount = applyLocationMultiplier(amount);
    return formatCurrency(adjustedAmount);
  };

  // Helper to get human-readable work configuration
  const getWorkConfigurationDescription = (periods) => {
    if (!periods || periods.length === 0) return resultsInfo.entireYearFullTimeText || "Full-time, entire year";

    if (periods.length === 1) {
      const period = periods[0];
      if (period.startMonth === 0 && period.endMonth === 12 && period.hoursPerWeek === 40) {
        return `Full-time, ${resultsInfo.entireYearText}`;
      } else if (period.startMonth === 0 && period.endMonth === 12) {
        return `${period.hoursPerWeek} ${resultsInfo.hoursPerWeekText}, ${resultsInfo.entireYearText}`;
      } else {
        const monthsInPeriod = period.endMonth - period.startMonth;
        return `${period.hoursPerWeek} ${resultsInfo.hoursPerWeekText}, ${resultsInfo.monthsText} ${period.startMonth}-${period.endMonth} (${monthsInPeriod} ${resultsInfo.monthsText})`;
      }
    }

    // Multiple periods
    return resultsInfo.multiplePeriodText || "Multiple periods with varying hours";
  };

  // Get the work environment object
  const selectedEnv = workEnvironments?.find(env => env.name === workEnvironment);
  const selectedOperatingResource = operatingResourcesOptions?.find(res => res.name === operatingResources);

  return (
    <div className="bg-gray-100 p-6 rounded-lg">
      <h2 className="text-2xl font-bold mb-4">{resultsInfo.title}</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center mb-2">
            <h3 className="text-lg font-semibold">{resultsInfo.costBreakdownTitle}</h3>
            <button
              className="ml-2 text-blue-500 text-sm"
              onClick={() => toggleAllCategories(Object.values(expandedCategories).some(v => !v))}
            >
              {Object.values(expandedCategories).every(v => v) ? resultsInfo.hideDetailsLabel : resultsInfo.showDetailsLabel}
            </button>
          </div>

          <div className="bg-white p-4 rounded shadow-sm">
            {/* Founders Salaries Section */}
            <div className="mb-4">
              <div
                className="flex justify-between items-center cursor-pointer hover:bg-gray-50 p-2 rounded"
                onClick={() => toggleCategory('founders')}
              >
                <div className="flex items-center">
                  <span className="mr-2">{expandedCategories.founders ? '▼' : '▶'}</span>
                  <span className="font-medium">{resultsInfo.foundersSalariesLabel}</span>
                </div>
                <span className="font-medium">{formatCurrency(applyLocationMultiplier(tristenFinalSalary + martinFinalSalary))}</span>
              </div>

              {expandedCategories.founders && (
                <div className="ml-8 mt-2 space-y-4 text-sm">
                  <div className="border-b pb-3">
                    <div className="flex justify-between font-medium">
                      <span>{resultsInfo.tristenFounderLabel}</span>
                      <span>{displaySalary(tristenFinalSalary)}</span>
                    </div>
                    <div className="mt-2">
                      <table className="w-full text-xs">
                        <tbody>
                          <tr>
                            <td className="text-gray-500 text-right">{resultsInfo.salaryTierLabel}</td>
                            <td className="text-right">{resultsInfo.tierLabel} {tristenSalaryTier}</td>
                          </tr>
                          <tr>
                            <td className="text-gray-500 text-right">{resultsInfo.amountLabel}</td>
                            <td className="text-right">{formatCurrency(salaryTiers[tristenSalaryTier].amount)}{resultsInfo.perYearText}</td>
                          </tr>
                          {eaType !== 'none' && (
                            <tr>
                              <td className="text-gray-500 text-right">{resultsInfo.eaSavingsLabel}</td>
                              <td className="text-right text-green-600">-{formatCurrency(20000)}</td>
                            </tr>
                          )}
                          {location === 'sanfrancisco' && (
                            <tr>
                              <td className="text-gray-500 text-right">{resultsInfo.locationLabel}</td>
                              <td className="text-right">{resultsInfo.sanFranciscoText} ({locationMultiplier}{resultsInfo.multiplierSymbolText || "×"})</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="border-b pb-3">
                    <div className="flex justify-between font-medium">
                      <span>{resultsInfo.martinFounderLabel}</span>
                      <span>{displaySalary(martinFinalSalary)}</span>
                    </div>
                    <div className="mt-2">
                      <table className="w-full text-xs">
                        <tbody>
                          <tr>
                            <td className="text-gray-500 text-right">{resultsInfo.salaryTierLabel}</td>
                            <td className="text-right">{resultsInfo.tierLabel} {martinSalaryTier}</td>
                          </tr>
                          <tr>
                            <td className="text-gray-500 text-right">{resultsInfo.amountLabel}</td>
                            <td className="text-right">{formatCurrency(salaryTiers[martinSalaryTier].amount)}{resultsInfo.perYearText}</td>
                          </tr>
                          {location === 'sanfrancisco' && (
                            <tr>
                              <td className="text-gray-500 text-right">{resultsInfo.locationLabel}</td>
                              <td className="text-right">{resultsInfo.sanFranciscoText} ({locationMultiplier}{resultsInfo.multiplierSymbolText || "×"})</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Hires Section */}
            {(hireDesigner || eaType !== 'none' || hireSocial) && (
              <div className="mb-4">
                <div
                  className="flex justify-between items-center cursor-pointer hover:bg-gray-50 p-2 rounded"
                  onClick={() => toggleCategory('hires')}
                >
                  <div className="flex items-center">
                    <span className="mr-2">{expandedCategories.hires ? '▼' : '▶'}</span>
                    <span className="font-medium">{resultsInfo.hiresLabel}</span>
                  </div>
                  <span className="font-medium">
                    {formatCurrency(
                      applyLocationMultiplier(
                        (hireDesigner ? calculateEffectiveSalaryForPeriods(designerSalary, designerPeriods, designerEmploymentType) : 0) +
                        (eaType !== 'none' ? calculateEffectiveSalaryForPeriods(eaSalary, eaPeriods, eaEmploymentType) : 0) +
                        (hireSocial ? calculateEffectiveSalaryForPeriods(socialSalary, socialPeriods, socialEmploymentType) : 0)
                      )
                    )}
                  </span>
                </div>

                {expandedCategories.hires && (
                  <div className="ml-8 mt-2 space-y-4 text-sm">
                    {hireDesigner && (
                      <div className="border-b pb-3">
                        <div className="flex justify-between font-medium">
                          <span>{resultsInfo.designerLabel}</span>
                          <span>{displaySalary(calculateEffectiveSalaryForPeriods(designerSalary, designerPeriods, designerEmploymentType))}</span>
                        </div>
                        <div className="mt-2 text-xs">
                          <table className="w-full text-xs">
                            <tbody>
                              <tr>
                                <td className="text-gray-500 text-right">{resultsInfo.salaryLabel}</td>
                                <td className="text-right">{formatCurrency(designerSalary)}{resultsInfo.perYearText}</td>
                              </tr>
                              <tr>
                                <td className="text-gray-500 text-right">{resultsInfo.employmentTypeLabel}</td>
                                <td className="text-right">{designerEmploymentType === 'contractor' ? resultsInfo.contractorText : resultsInfo.employeeText}</td>
                              </tr>
                              {designerPeriods && designerPeriods.map((period, index) => (
                                <tr key={`designer-period-${index}`}>
                                  <td className="text-gray-500 text-right">
                                    {designerPeriods.length > 1 ? `${resultsInfo.periodLabel} ${index + 1}:` : resultsInfo.scheduleLabel}
                                  </td>
                                  <td className="text-right">
                                    {period.hoursPerWeek} {resultsInfo.hoursPerWeekText},
                                    {period.startMonth === 0 && period.endMonth === 12
                                      ? ` ${resultsInfo.entireYearText}`
                                      : ` ${resultsInfo.monthsText} ${period.startMonth}-${period.endMonth} (${period.endMonth - period.startMonth} ${resultsInfo.monthsText})`}
                                  </td>
                                </tr>
                              ))}
                              {location === 'sanfrancisco' && (
                                <tr>
                                  <td className="text-gray-500 text-right">{resultsInfo.locationLabel}</td>
                                  <td className="text-right">{resultsInfo.sanFranciscoText} ({locationMultiplier}{resultsInfo.multiplierSymbolText || "×"})</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {eaType !== 'none' && (
                      <div className="border-b pb-3">
                        <div className="flex justify-between font-medium">
                          <span>{eaType === 'clinical' ? resultsInfo.eaClinicalLabel.slice(0, -1) : resultsInfo.eaRegularLabel.slice(0, -1)}</span>
                          <span>{displaySalary(calculateEffectiveSalaryForPeriods(eaSalary, eaPeriods, eaEmploymentType))}</span>
                        </div>
                        <div className="mt-2 text-xs">
                          <table className="w-full text-xs">
                            <tbody>
                              <tr>
                                <td className="text-gray-500 text-right">{resultsInfo.salaryLabel}</td>
                                <td className="text-right">{formatCurrency(eaSalary)}{resultsInfo.perYearText}</td>
                              </tr>
                              <tr>
                                <td className="text-gray-500 text-right">{resultsInfo.employmentTypeLabel}</td>
                                <td className="text-right">{eaEmploymentType === 'contractor' ? resultsInfo.contractorText : resultsInfo.employeeText}</td>
                              </tr>
                              {eaPeriods && eaPeriods.map((period, index) => (
                                <tr key={`ea-period-${index}`}>
                                  <td className="text-gray-500 text-right">
                                    {eaPeriods.length > 1 ? `${resultsInfo.periodLabel} ${index + 1}:` : `${resultsInfo.scheduleLabel}:`}
                                  </td>
                                  <td className="text-right">
                                    {period.hoursPerWeek} {resultsInfo.hoursPerWeekText},
                                    {period.startMonth === 0 && period.endMonth === 12
                                      ? ` ${resultsInfo.entireYearText}`
                                      : ` ${resultsInfo.monthsText} ${period.startMonth}-${period.endMonth} (${period.endMonth - period.startMonth} ${resultsInfo.monthsText})`}
                                  </td>
                                </tr>
                              ))}
                              <tr>
                                <td className="text-gray-500 text-right">{resultsInfo.eaSavingsLabel}</td>
                                <td className="text-right text-green-600">-{formatCurrency(calculateEffectiveSalaryForPeriods(hiresConfig.ea.tristenSavings, eaPeriods, 'employee'))}</td>
                              </tr>
                              {location === 'sanfrancisco' && (
                                <tr>
                                  <td className="text-gray-500 text-right">{resultsInfo.locationLabel}</td>
                                  <td className="text-right">{resultsInfo.sanFranciscoText} ({locationMultiplier}{resultsInfo.multiplierSymbolText || "×"})</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {hireSocial && (
                      <div className="border-b pb-3">
                        <div className="flex justify-between font-medium">
                          <span>{resultsInfo.socialMediaLabel.slice(0, -1)}</span>
                          <span>{displaySalary(calculateEffectiveSalaryForPeriods(socialSalary, socialPeriods, socialEmploymentType))}</span>
                        </div>
                        <div className="mt-2 text-xs">
                          <table className="w-full text-xs">
                            <tbody>
                              <tr>
                                <td className="text-gray-500 text-right">{resultsInfo.salaryLabel}</td>
                                <td className="text-right">{formatCurrency(socialSalary)}{resultsInfo.perYearText}</td>
                              </tr>
                              <tr>
                                <td className="text-gray-500 text-right">{resultsInfo.employmentTypeLabel}</td>
                                <td className="text-right">{socialEmploymentType === 'contractor' ? resultsInfo.contractorText : resultsInfo.employeeText}</td>
                              </tr>
                              {socialPeriods && socialPeriods.map((period, index) => (
                                <tr key={`social-period-${index}`}>
                                  <td className="text-gray-500 text-right">
                                    {socialPeriods.length > 1 ? `${resultsInfo.periodLabel} ${index + 1}:` : `${resultsInfo.scheduleLabel}:`}
                                  </td>
                                  <td className="text-right">
                                    {period.hoursPerWeek} {resultsInfo.hoursPerWeekText},
                                    {period.startMonth === 0 && period.endMonth === 12
                                      ? ` ${resultsInfo.entireYearText}`
                                      : ` ${resultsInfo.monthsText} ${period.startMonth}-${period.endMonth} (${period.endMonth - period.startMonth} ${resultsInfo.monthsText})`}
                                  </td>
                                </tr>
                              ))}
                              {location === 'sanfrancisco' && (
                                <tr>
                                  <td className="text-gray-500 text-right">{resultsInfo.locationLabel}</td>
                                  <td className="text-right">{resultsInfo.sanFranciscoText} ({locationMultiplier}{resultsInfo.multiplierSymbolText || "×"})</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Work Environment Section */}
            <div className="mb-4">
              <div
                className="flex justify-between items-center cursor-pointer hover:bg-gray-50 p-2 rounded"
                onClick={() => toggleCategory('workEnv')}
              >
                <div className="flex items-center">
                  <span className="mr-2">{expandedCategories.workEnv ? '▼' : '▶'}</span>
                  <span className="font-medium">{resultsInfo.workEnvironmentLabel}</span>
                </div>
                <span className="font-medium">{formatCurrency(totalEnvironmentCosts)}</span>
              </div>

              {expandedCategories.workEnv && selectedEnv && (
                <div className="ml-8 mt-2 space-y-1 text-sm">
                  <div className="border-b pb-3">
                    <div className="flex justify-between font-medium">
                      <span>{selectedEnv.description.split(':')[0]}</span>
                      <span>{displaySalary(selectedEnv.cost)}</span>
                    </div>
                    <div className="mt-2">
                      <table className="w-full text-xs">
                        <tbody>
                          <tr>
                            <td className="text-gray-500 text-right">{resultsInfo.annualCostLabel}</td>
                            <td className="text-right">{formatCurrency(selectedEnv.cost)}{resultsInfo.perYearText}</td>
                          </tr>
                          {location === 'sanfrancisco' && (
                            <tr>
                              <td className="text-gray-500 text-right">{resultsInfo.locationLabel}</td>
                              <td className="text-right">{resultsInfo.sanFranciscoText} ({locationMultiplier}{resultsInfo.multiplierSymbolText || "×"})</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Operating Resources Section */}
            <div className="mb-4">
              <div
                className="flex justify-between items-center cursor-pointer hover:bg-gray-50 p-2 rounded"
                onClick={() => toggleCategory('operating')}
              >
                <div className="flex items-center">
                  <span className="mr-2">{expandedCategories.operating ? '▼' : '▶'}</span>
                  <span className="font-medium">{resultsInfo.operatingResourcesLabel}</span>
                </div>
                <span className="font-medium">{formatCurrency(totalOperatingCosts)}</span>
              </div>

              {expandedCategories.operating && selectedOperatingResource && (
                <div className="ml-8 mt-2 space-y-1 text-sm">
                  <div className="border-b pb-3">
                    <div className="flex justify-between font-medium">
                      <span>{selectedOperatingResource.description.split(':')[0]}</span>
                      <span>{displaySalary(selectedOperatingResource.costPerMonth * 12)}</span>
                    </div>
                    <div className="mt-2">
                      <table className="w-full text-xs">
                        <tbody>
                          <tr>
                            <td className="text-gray-500 text-right">{resultsInfo.monthlyCostLabel}</td>
                            <td className="text-right">{formatCurrency(selectedOperatingResource.costPerMonth)}{resultsInfo.perMonthText}</td>
                          </tr>
                          <tr>
                            <td className="text-gray-500 text-right">{resultsInfo.annualCostLabel}</td>
                            <td className="text-right">{formatCurrency(selectedOperatingResource.costPerMonth * 12)}{resultsInfo.perYearText}</td>
                          </tr>
                          {location === 'sanfrancisco' && (
                            <tr>
                              <td className="text-gray-500 text-right">{resultsInfo.locationLabel}</td>
                              <td className="text-right">{resultsInfo.sanFranciscoText} ({locationMultiplier}{resultsInfo.multiplierSymbolText || "×"})</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Additional Costs Section */}
            {totalAdditionalCosts > 0 && (
              <div className="mb-4">
                <div
                  className="flex justify-between items-center cursor-pointer hover:bg-gray-50 p-2 rounded"
                  onClick={() => toggleCategory('additional')}
                >
                  <div className="flex items-center">
                    <span className="mr-2">{expandedCategories.additional ? '▼' : '▶'}</span>
                    <span className="font-medium">{resultsInfo.additionalCostsLabel}</span>
                  </div>
                  <span className="font-medium">{formatCurrency(totalAdditionalCosts)}</span>
                </div>

                {expandedCategories.additional && (
                  <div className="ml-8 mt-2 space-y-4 text-sm">
                    <div className="border-b pb-3">
                      <div className="flex justify-between font-medium">
                        <span>{resultsInfo.additionalCostsBreakdownLabel}</span>
                        <span>{formatCurrency(totalAdditionalCosts)}{resultsInfo.perYearText}</span>
                      </div>
                      <div className="mt-2">
                        <table className="w-full text-xs">
                          <tbody>
                            {legalCosts > 0 && (
                              <tr>
                                <td className="text-gray-500 text-right">{resultsInfo.legalCostsLabel}</td>
                                <td className="text-right">{formatCurrency(legalCosts)}{resultsInfo.perYearText}</td>
                              </tr>
                            )}
                            {travelCosts > 0 && (
                              <tr>
                                <td className="text-gray-500 text-right">{resultsInfo.travelCostsLabel}</td>
                                <td className="text-right">{formatCurrency(travelCosts)}{resultsInfo.perYearText}</td>
                              </tr>
                            )}
                            {additionalCosts > 0 && (
                              <tr>
                                <td className="text-gray-500 text-right">{resultsInfo.otherCostsLabel}</td>
                                <td className="text-right">{formatCurrency(additionalCosts)}{resultsInfo.perYearText}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Equity Costs Section */}
            {((hireDesigner && designerEmploymentType !== 'contractor' && designerEquity > 0) ||
              (eaType !== 'none' && eaEmploymentType !== 'contractor' && eaEquity > 0) ||
              (hireSocial && socialEmploymentType !== 'contractor' && socialEquity > 0)) && (
              <div className="mb-4 border-t pt-4">
                <div
                  className="flex justify-between items-center cursor-pointer hover:bg-gray-50 p-2 rounded"
                  onClick={() => toggleCategory('equity')}
                >
                  <div className="flex items-center">
                    <span className="mr-2">{expandedCategories.equity ? '▼' : '▶'}</span>
                    <span className="font-medium">{resultsInfo.equityCostsLabel}</span>
                  </div>
                  <span className="font-medium text-red-600">
                    -{((hireDesigner && designerEmploymentType !== 'contractor' ? designerEquity : 0) +
                      (eaType !== 'none' && eaEmploymentType !== 'contractor' ? eaEquity : 0) +
                      (hireSocial && socialEmploymentType !== 'contractor' ? socialEquity : 0)).toFixed(2)}%
                  </span>
                </div>

                {expandedCategories.equity && (
                  <div className="ml-8 mt-2 space-y-4 text-sm">
                    <div className="border-b pb-3">
                      <div className="flex justify-between font-medium">
                        <span>{resultsInfo.equityDistributionLabel}</span>
                        <span className="text-red-600">
                          -{((hireDesigner && designerEmploymentType !== 'contractor' ? designerEquity : 0) +
                            (eaType !== 'none' && eaEmploymentType !== 'contractor' ? eaEquity : 0) +
                            (hireSocial && socialEmploymentType !== 'contractor' ? socialEquity : 0)).toFixed(2)}%
                        </span>
                      </div>
                      <div className="mt-2">
                        <table className="w-full text-xs">
                          <tbody>
                            {hireDesigner && designerEmploymentType !== 'contractor' && designerEquity > 0 && (
                              <tr>
                                <td className="text-gray-500 text-right">{resultsInfo.designerLabel}:</td>
                                <td className="text-right text-red-600">-{designerEquity.toFixed(2)}%</td>
                              </tr>
                            )}
                            {eaType !== 'none' && eaEmploymentType !== 'contractor' && eaEquity > 0 && (
                              <tr>
                                <td className="text-gray-500 text-right">{eaType === 'clinical' ? resultsInfo.eaClinicalLabel : resultsInfo.eaRegularLabel}</td>
                                <td className="text-right text-red-600">-{eaEquity.toFixed(2)}%</td>
                              </tr>
                            )}
                            {hireSocial && socialEmploymentType !== 'contractor' && socialEquity > 0 && (
                              <tr>
                                <td className="text-gray-500 text-right">{resultsInfo.socialMediaLabel}</td>
                                <td className="text-right text-red-600">-{socialEquity.toFixed(2)}%</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Total */}
            <div className="border-t pt-3 space-y-2">
              <div className="flex justify-between font-bold text-lg">
                <span>{resultsInfo.totalAnnualCostLabel}</span>
                <span>
                  {formatCurrency(totalCosts)}
                  {location === 'sanfrancisco' && ` (${resultsInfo.sfShortText || "SF"} ${locationMultiplier}${resultsInfo.multiplierSymbolText || "×"})`}
                </span>
              </div>

              {/* Total Equity Cost */}
              {(designerEquity > 0 || eaEquity > 0 || socialEquity > 0) && (
                <div>
                  <div className="flex justify-between font-bold text-lg">
                    <span>{resultsInfo.totalEquityCostLabel}</span>
                    <span className="text-red-600">
                      -{((hireDesigner && designerEmploymentType !== 'contractor' ? designerEquity : 0) +
                        (eaType !== 'none' && eaEmploymentType !== 'contractor' ? eaEquity : 0) +
                        (hireSocial && socialEmploymentType !== 'contractor' ? socialEquity : 0)).toFixed(2)}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center mb-2">
            <h3 className="text-lg font-semibold">{resultsInfo.successProbabilityTitle}</h3>
            <button
              className="ml-2 text-blue-500 text-sm"
              onClick={() => setShowSuccessDetails(!showSuccessDetails)}
            >
              {showSuccessDetails ? resultsInfo.hideDetailsLabel : resultsInfo.showDetailsLabel}
            </button>
          </div>

          <div className="bg-white p-4 rounded shadow-sm">
            {/* Success Rate Breakdown */}
            <div className="mb-4">
              <div className="flex justify-between mb-2">
                <span>{resultsInfo.baselineRateLabel}</span>
                <span className="font-medium">{baseSuccessRate}%</span>
              </div>

              {/* Fixed Factors */}
              <div className="mb-3">
                <div className="flex justify-between font-medium mb-1">
                  <span>{resultsInfo.fixedFactorModifiersLabel}</span>
                  <span className={successRateModifier >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {successRateModifier >= 0 ? `+${successRateModifier.toFixed(1)}%` : `${successRateModifier.toFixed(1)}%`}
                  </span>
                </div>
                {showSuccessDetails && (
                  <div className="ml-4 text-sm space-y-1">
                    <div className="flex justify-between">
                      <span>{resultsInfo.tristenSalaryLabel} ({resultsInfo.tierLabel} {tristenSalaryTier})</span>
                      <span className={salaryTiers[tristenSalaryTier].modifier >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {salaryTiers[tristenSalaryTier].modifier >= 0 ? '+' : ''}{salaryTiers[tristenSalaryTier].modifier}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{resultsInfo.martinSalaryLabel} ({resultsInfo.tierLabel} {martinSalaryTier})</span>
                      <span className={salaryTiers[martinSalaryTier].modifier >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {salaryTiers[martinSalaryTier].modifier >= 0 ? '+' : ''}{salaryTiers[martinSalaryTier].modifier}%
                      </span>
                    </div>
                    {selectedEnv && (
                      <div className="flex justify-between">
                        <span>{selectedEnv.description.split(':')[0]}</span>
                        <span className={selectedEnv.modifier >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {selectedEnv.modifier >= 0 ? '+' : ''}{selectedEnv.modifier}%
                        </span>
                      </div>
                    )}
                    {selectedOperatingResource && (
                      <div className="flex justify-between">
                        <span>{selectedOperatingResource.description.split(':')[0]}</span>
                        <span className={selectedOperatingResource.modifier >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {selectedOperatingResource.modifier >= 0 ? '+' : ''}{selectedOperatingResource.modifier}%
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Potential Hire Impact */}
              <div className="mb-3">
                <div className="flex justify-between font-medium mb-1">
                  <span>{resultsInfo.potentialHireImpactLabel}</span>
                  <span className="text-blue-600">
                    +0.0% to +{(finalSuccessRate.max - finalSuccessRate.min).toFixed(1)}%
                  </span>
                </div>
                {showSuccessDetails && (
                  <div className="ml-4 text-sm space-y-1">
                    {hireDesigner && (
                      <div className="flex justify-between">
                        <span>{resultsInfo.designerTitleText}</span>
                        <span className="text-blue-600">{resultsInfo.designerImpactText}</span>
                      </div>
                    )}
                    {eaType === 'regular' && (
                      <div className="flex justify-between">
                        <span>{resultsInfo.eaRegularTitleText}</span>
                        <span className="text-blue-600">{resultsInfo.eaRegularImpactText}</span>
                      </div>
                    )}
                    {eaType === 'clinical' && (
                      <div className="flex justify-between">
                        <span>{resultsInfo.eaOrgPsychTitleText}</span>
                        <span className="text-blue-600">{resultsInfo.eaOrgPsychImpactText}</span>
                      </div>
                    )}
                    {hireSocial && (
                      <div className="flex justify-between">
                        <span>{resultsInfo.socialMediaTitleText}</span>
                        <span className="text-blue-600">{resultsInfo.socialMediaImpactText}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Final Results */}
            <div className="border-t pt-3">
              <div className="flex justify-between mb-2 font-bold">
                <span>{resultsInfo.estimatedSuccessRateLabel}</span>
                <span className="text-xl text-blue-700">
                  {averageSuccessRate}%
                </span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>{resultsInfo.successRangeLabel}</span>
                <span>
                  {finalSuccessRate.min}% to {finalSuccessRate.max}%
                </span>
              </div>
            </div>
          </div>

          {/* Success rating interpretations based on average */}
          <div className="mt-4 p-3 bg-gray-200 rounded text-sm">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium">{resultsInfo.interpretationTitle}</h4>
              <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                {resultsInfo.basedOnAvgLabel.replace('{avg}', averageSuccessRate)}
              </span>
            </div>
            <p className={interpretation.colorClass}>
              <strong>{interpretation.text.split(':')[0]}:</strong>
              {interpretation.text.split(':')[1]}
            </p>

            <div className="mt-3">
              <div className="w-full bg-gray-300 rounded-full h-4">
                <div
                  className={`h-4 rounded-full ${
                    averageSuccessRate < 5 ? 'bg-red-600' : 
                    averageSuccessRate < 10 ? 'bg-yellow-500' : 
                    averageSuccessRate < 15 ? 'bg-blue-500' : 
                    averageSuccessRate < 20 ? 'bg-green-500' : 'bg-green-700'
                  }`}
                  style={{ width: `${Math.min(averageSuccessRate * 2, 100)}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span>{resultsInfo.scaleZeroLabel}</span>
                <span>{resultsInfo.scaleTenLabel}</span>
                <span>{resultsInfo.scaleTwentyLabel}</span>
                <span>{resultsInfo.scaleThirtyLabel}</span>
                <span>{resultsInfo.scaleFiftyPlusLabel}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* NEW: Culture and Capitalization Scores section */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Culture Score */}
        <div>
          <h3 className="text-lg font-semibold mb-2">{resultsInfo.cultureScoreTitle}</h3>
          <div className="bg-white p-4 rounded shadow-sm">
            <div className="mb-4">
              <div className="flex justify-between items-baseline mb-2">
                <span className="text-3xl font-bold">{cultureScore.toFixed(1)}</span>
                <span className={`text-lg font-medium ${getCultureLevel(cultureScore).color}`}>
                  {getCultureLevel(cultureScore).level}
                </span>
              </div>

              {/* Culture Score Bar */}
              <div className="w-full bg-gray-300 rounded-full h-4 mb-2">
                <div
                  className={`h-4 rounded-full ${
                    cultureScore >= 85 ? 'bg-green-700' : 
                    cultureScore >= 70 ? 'bg-green-600' : 
                    cultureScore >= 55 ? 'bg-blue-500' : 
                    cultureScore >= 40 ? 'bg-yellow-500' : 'bg-red-600'
                  }`}
                  style={{ width: `${Math.min(cultureScore, 100)}%` }}
                ></div>
              </div>

              <p className="text-sm text-gray-600">
                {getCultureLevel(cultureScore).description}
              </p>
            </div>

            {/* Culture Factors Breakdown */}
            <div className="border-t pt-3">
              <h4 className="font-medium mb-2">{resultsInfo.cultureFactorsLabel}</h4>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span>{resultsInfo.teamSizeLabel}</span>
                  <span className="text-gray-600">
                    {2 + (hireDesigner ? 1 : 0) + (eaType !== 'none' ? 1 : 0) + (hireSocial ? 1 : 0)} {resultsInfo.teamMembersText}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{resultsInfo.workEnvironmentLabel}</span>
                  <span className="text-gray-600">
                    {workEnvironment === 'garage' ? resultsInfo.basicText :
                     workEnvironment === 'coworking' ? resultsInfo.collaborativeText : resultsInfo.professionalText}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{resultsInfo.founderComfortLabel}</span>
                  <span className="text-gray-600">
                    {resultsInfo.tierLabel} {Math.round((tristenSalaryTier + martinSalaryTier) / 2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{resultsInfo.operatingResourcesLabel}</span>
                  <span className="text-gray-600">
                    {operatingResources === 'survival' ? resultsInfo.minimalText :
                     operatingResources === 'sustainable' ? resultsInfo.basicText :
                     operatingResources === 'enabled' ? resultsInfo.goodText : resultsInfo.excellentText}
                  </span>
                </div>
                {eaType !== 'none' && (
                  <div className="flex justify-between">
                    <span>{resultsInfo.eaSupportLabel}</span>
                    <span className="text-green-600">
                      {eaType === 'clinical' ? resultsInfo.eaOrgPsychSupportText : resultsInfo.eaRegularSupportText}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Capitalization Score */}
        <div>
          <h3 className="text-lg font-semibold mb-2">{resultsInfo.burnScoreTitle}</h3>
          <div className="bg-white p-4 rounded shadow-sm">
            <div className="mb-4">
              <div className="flex justify-between items-baseline mb-2">
                <span className="text-3xl font-bold">{capitalizationScore.toFixed(1)}</span>
                <span className={`text-lg font-medium ${getCapitalizationLevel(capitalizationScore).color}`}>
                  {getCapitalizationLevel(capitalizationScore).level}
                </span>
              </div>

              {/* Capitalization Score Bar */}
              <div className="w-full bg-gray-300 rounded-full h-4 mb-2">
                <div
                  className={`h-4 rounded-full ${
                    capitalizationScore >= 85 ? 'bg-green-700' : 
                    capitalizationScore >= 70 ? 'bg-green-600' : 
                    capitalizationScore >= 55 ? 'bg-blue-500' : 
                    capitalizationScore >= 40 ? 'bg-yellow-500' : 'bg-red-600'
                  }`}
                  style={{ width: `${Math.min(capitalizationScore, 100)}%` }}
                ></div>
              </div>

              <p className="text-sm text-gray-600">
                {getCapitalizationLevel(capitalizationScore).description}
              </p>
            </div>

            {/* Capitalization Metrics */}
            <div className="border-t pt-3">
              <h4 className="font-medium mb-2">{resultsInfo.keyMetricsLabel}</h4>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span>{resultsInfo.annualBurnRateLabel}</span>
                  <span className="font-medium">{formatCurrency(totalCosts)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{resultsInfo.monthlyBurnLabel}</span>
                  <span className="font-medium">{formatCurrency(totalCosts / 12)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{resultsInfo.totalEquityGivenLabel}</span>
                  <span className="font-medium text-red-600">
                    {((hireDesigner && designerEmploymentType !== 'contractor' ? designerEquity : 0) +
                      (eaType !== 'none' && eaEmploymentType !== 'contractor' ? eaEquity : 0) +
                      (hireSocial && socialEmploymentType !== 'contractor' ? socialEquity : 0)).toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{resultsInfo.runwayWithLabel || "Runway with $1M"}</span>
                  <span className="font-medium">
                    {Math.round((1000000 / totalCosts) * 12)} {resultsInfo.monthsText}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsDisplay;